import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, X, ShoppingCart, Image as ImageIcon, Plus, Eye, Check, ChevronRight, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Logo } from "../components/Logo";
import { BACKEND_URL, API } from "../utils/format";
import { formatMoney } from "../utils/currency";

// ============ PUBLIC PORTAL ============
// Public portal has no auth/CompanyContext — company info comes from the /portal/{code} response.
const makeFm = (company) => (v) => formatMoney(v, company);

const PublicPortal = () => {
  const { accessCode } = useParams();
  const [data, setData] = useState(null);
  const fm = makeFm(data?.company);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(`cart_${accessCode}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState({
    customer_name: "", company: "", vat_number: "",
    email: "", phone: "", address: "", city: "",
    notes: "", special_requests: ""
  });

  useEffect(() => {
    localStorage.setItem(`cart_${accessCode}`, JSON.stringify(cart));
  }, [cart, accessCode]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (selectedCategory !== "all") params.append("category_id", selectedCategory);
        const response = await axios.get(`${API}/portal/${accessCode}?${params}`);
        setData(response.data);
      } catch (error) {
        setError(error.response?.data?.detail || "Errore");
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchData, 300);
    return () => clearTimeout(debounce);
  }, [accessCode, search, selectedCategory]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.line_total, 0);

  const addToCart = (product, quantity, unit) => {
    if (!quantity || quantity <= 0) {
      toast.error("Inserisci una quantità valida");
      return;
    }
    const line_total = +(quantity * product.price).toFixed(2);
    const item = {
      product_id: product.id,
      code: product.code,
      description: product.description,
      quantity: +quantity,
      unit,
      unit_price: product.price,
      line_total,
      image_path: product.image_path || null,
      note: "",
    };
    setCart(prev => {
      const idx = prev.findIndex(p => p.product_id === product.id && p.unit === unit);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: +(copy[idx].quantity + +quantity), line_total: +(copy[idx].line_total + line_total).toFixed(2) };
        return copy;
      }
      return [...prev, item];
    });
    toast.success(`${product.description} aggiunto al carrello`);
    setSelectedProduct(null);
  };

  const removeFromCart = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));
  const updateCartQty = (idx, qty) => {
    if (qty <= 0) return removeFromCart(idx);
    setCart(prev => prev.map((it, i) => i === idx ? { ...it, quantity: +qty, line_total: +(qty * it.unit_price).toFixed(2) } : it));
  };

  const submitOrder = async () => {
    const required = ["customer_name", "email", "phone", "company", "address"];
    const missing = required.filter(k => !customer[k] || !String(customer[k]).trim());
    if (missing.length > 0) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (cart.length === 0) {
      toast.error("Il carrello è vuoto");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer,
        items: cart.map(({ image_path, ...rest }) => rest),
      };
      const res = await axios.post(`${API}/portal/${accessCode}/order`, payload);
      setOrderSuccess(res.data);
      setCart([]);
      localStorage.removeItem(`cart_${accessCode}`);
      setShowCheckout(false);
      setShowCart(false);
      setCustomer({ customer_name: "", company: "", vat_number: "", email: "", phone: "", address: "", city: "", notes: "", special_requests: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio ordine");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="spinner w-8 h-8" /></div>;
  if (error) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><p className="text-red-500">{error}</p></div>;

  const grouped = data?.products?.reduce((acc, prod) => {
    const cat = prod.category_name || "Altri";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(prod);
    return acc;
  }, {}) || {};

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <header className="sticky top-0 z-40 bg-white border-b border-stone-200 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-10" />
            <div>
              <p className="text-stone-900 font-semibold">{data?.agent?.name}</p>
              <p className="text-xs text-[#8A1C2A]">Listino & Ordini</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCart(true)}
            className="relative bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
            data-testid="open-cart-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Carrello
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C5A059] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Cerca prodotto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="portal-search" />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-56 bg-white border-stone-300"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {data?.categories?.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {Object.entries(grouped).map(([category, products]) => (
          <div key={category} className="mb-8">
            <h2 className="text-xl font-semibold text-[#8A1C2A] mb-4 uppercase">{category}</h2>
            <Card className="bg-white border-stone-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#8A1C2A]">
                    <th className="text-left p-3 text-xs font-semibold text-white w-16">Foto</th>
                    <th className="text-left p-3 text-xs font-semibold text-white">Codice</th>
                    <th className="text-left p-3 text-xs font-semibold text-white">Descrizione</th>
                    <th className="text-center p-3 text-xs font-semibold text-white w-20">U.M.</th>
                    <th className="text-right p-3 text-xs font-semibold text-white w-28">Prezzo</th>
                    <th className="text-center p-3 text-xs font-semibold text-white w-32">Disp.</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((prod) => (
                    <tr
                      key={prod.id}
                      className="border-b border-stone-200 hover:bg-[#C5A059]/5 cursor-pointer transition-colors"
                      onClick={() => setSelectedProduct(prod)}
                      data-testid={`portal-product-${prod.code}`}
                    >
                      <td className="p-2">
                        {prod.image_path ? (
                          <img src={prod.image_path.startsWith('http') ? prod.image_path : `${BACKEND_URL}/api/files/${prod.image_path}`} alt={prod.description} className="w-12 h-12 object-cover rounded border border-stone-200" />
                        ) : (
                          <div className="w-12 h-12 rounded border border-stone-200 bg-stone-100 flex items-center justify-center">
                            <ImageIcon className="w-5 h-5 text-stone-400" />
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-sm font-mono text-stone-600">{prod.code}</td>
                      <td className="p-3 text-sm text-stone-900 font-medium">{prod.description}</td>
                      <td className="p-3 text-xs text-center text-stone-600">{prod.unit || "Kg"}</td>
                      <td className="p-3 text-right text-base font-semibold text-[#8A1C2A]">{prod.price > 0 ? `${fm(prod.price)}` : "—"}</td>
                      <td className="p-3 text-center">
                        <Badge className={prod.availability === "DISPONIBILE" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>{prod.availability || "-"}</Badge>
                      </td>
                      <td className="p-3 text-right text-stone-400">
                        <ChevronRight className="w-4 h-4 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        ))}
      </div>

      {/* Sticky Cart bar (mobile-friendly) */}
      {cartCount > 0 && !showCart && !showCheckout && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#8A1C2A] text-white p-4 shadow-2xl z-30">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <p className="font-semibold">{cartCount} articolo/i nel carrello</p>
              <p className="text-sm text-[#C5A059]">Totale: <strong>{fm(cartTotal)}</strong></p>
            </div>
            <Button onClick={() => setShowCart(true)} className="bg-[#C5A059] hover:bg-[#a88848] text-white" data-testid="view-cart-btn">
              <ShoppingBag className="w-4 h-4 mr-2" /> Vedi carrello
            </Button>
          </div>
        </div>
      )}

      <footer className="border-t border-stone-200 py-6 text-center text-sm text-stone-500 bg-white">
        © {new Date().getFullYear()} Giotto Carni — Prezzi IVA esclusa
      </footer>

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAdd={addToCart}
        company={data?.company}
      />

      {/* Cart Drawer */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="cart-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#8A1C2A]" />
              Il tuo carrello
            </DialogTitle>
          </DialogHeader>
          {cart.length === 0 ? (
            <div className="py-12 text-center text-stone-500">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p>Il carrello è vuoto</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {cart.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg" data-testid={`cart-item-${idx}`}>
                    {item.image_path ? (
                      <img src={item.image_path.startsWith('http') ? item.image_path : `${BACKEND_URL}/api/files/${item.image_path}`} alt={item.description} className="w-14 h-14 object-cover rounded" />
                    ) : (
                      <div className="w-14 h-14 rounded bg-stone-100 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-stone-400" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-stone-500">{item.code}</p>
                      <p className="text-sm font-medium text-stone-900 truncate">{item.description}</p>
                      <p className="text-xs text-stone-500">{fm(item.unit_price)} / {item.unit}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCartQty(idx, Math.max(0, item.quantity - 1))}><Minus className="w-3 h-3" /></Button>
                      <Input type="number" step="0.1" min="0" value={item.quantity} onChange={(e) => updateCartQty(idx, parseFloat(e.target.value) || 0)} className="h-7 w-16 text-center text-sm bg-white border-stone-300" data-testid={`cart-qty-${idx}`} />
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCartQty(idx, item.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                      <span className="text-xs text-stone-500 ml-1 w-8">{item.unit}</span>
                    </div>
                    <div className="text-right w-24">
                      <p className="font-semibold text-[#8A1C2A]">{fm(item.line_total)}</p>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeFromCart(idx)} data-testid={`remove-cart-${idx}`}>
                      <Trash2 className="w-4 h-4 text-stone-400 hover:text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="border-t border-stone-200 pt-4 mt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Totale ordine</p>
                  <p className="text-2xl font-bold text-[#8A1C2A]">{fm(cartTotal)}</p>
                  <p className="text-xs text-stone-400">IVA esclusa</p>
                </div>
                <Button onClick={() => { setShowCart(false); setShowCheckout(true); }} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="proceed-checkout-btn">
                  Procedi al checkout <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-900">Dati per la consegna</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-700">Nome e Cognome *</Label>
              <Input value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-name" />
            </div>
            <div>
              <Label className="text-stone-700">Email *</Label>
              <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-email" />
            </div>
            <div>
              <Label className="text-stone-700">Telefono *</Label>
              <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-phone" />
            </div>
            <div>
              <Label className="text-stone-700">Azienda / Ragione Sociale *</Label>
              <Input value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-company" />
            </div>
            <div>
              <Label className="text-stone-700">P. IVA / CF</Label>
              <Input value={customer.vat_number} onChange={(e) => setCustomer({ ...customer, vat_number: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-vat" />
            </div>
            <div>
              <Label className="text-stone-700">Città</Label>
              <Input value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className="bg-white border-stone-300" data-testid="checkout-city" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-stone-700">Indirizzo di consegna *</Label>
              <Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} placeholder="Via, civico, CAP, città" className="bg-white border-stone-300" data-testid="checkout-address" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-stone-700">Richieste speciali / Note libere</Label>
              <textarea
                value={customer.special_requests}
                onChange={(e) => setCustomer({ ...customer, special_requests: e.target.value })}
                rows={3}
                placeholder="Es. consegna entro venerdì, taglio specifico, ecc."
                className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C2A]/30"
                data-testid="checkout-notes"
              />
            </div>
          </div>

          <div className="border-t border-stone-200 pt-4 mt-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-stone-700">Riepilogo ordine ({cart.length} articolo/i)</p>
              <p className="text-xl font-bold text-[#8A1C2A]">{fm(cartTotal)}</p>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1 mb-4">
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between text-xs text-stone-600">
                  <span className="truncate">{item.quantity} {item.unit} — {item.description}</span>
                  <span className="ml-2 font-semibold">{fm(item.line_total)}</span>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCheckout(false); setShowCart(true); }} className="border-stone-300">Torna al carrello</Button>
            <Button onClick={submitOrder} disabled={submitting} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="submit-order-btn">
              {submitting ? "Invio in corso..." : `Conferma ordine — ${fm(cartTotal)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Success Modal */}
      <Dialog open={!!orderSuccess} onOpenChange={() => setOrderSuccess(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="order-success-dialog">
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2">Ordine inviato!</h3>
            <p className="text-sm text-stone-600 mb-1">Numero ordine</p>
            <p className="text-2xl font-mono font-bold text-[#8A1C2A] mb-4">{orderSuccess?.order_number}</p>
            <p className="text-sm text-stone-600 mb-4">Ti contatteremo a breve per confermare la consegna.</p>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`*Ordine GIOTTO CARNI ${orderSuccess?.order_number}*\nCliente: ${customer.customer_name || orderSuccess?.customer_name || ''}\nAgente: ${data?.agent?.name || ''}\nTotale: ${fm(cartTotal)}\nGrazie!`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1da855] text-white text-sm font-semibold rounded-md transition-colors mb-2"
              data-testid="share-whatsapp-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
              Invia su WhatsApp
            </a>
            <Button onClick={() => setOrderSuccess(null)} variant="outline" className="mt-2 w-full border-stone-300">Chiudi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProductDetailModal = ({ product, onClose, onAdd, company }) => {
  const fm = makeFm(company);
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("Kg");

  useEffect(() => {
    if (product) {
      setQuantity(1);
      setUnit(product.unit || "Kg");
    }
  }, [product]);

  if (!product) return null;

  const lineTotal = quantity * (product.price || 0);

  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white" data-testid="product-modal">
        <DialogHeader>
          <DialogTitle className="text-stone-900">{product.description}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            {product.image_path ? (
              <img
                src={product.image_path.startsWith('http') ? product.image_path : `${BACKEND_URL}/api/files/${product.image_path}`}
                alt={product.description}
                className="w-full h-64 object-cover rounded-lg border border-stone-200"
              />
            ) : (
              <div className="w-full h-64 bg-stone-100 rounded-lg flex items-center justify-center border border-stone-200">
                <ImageIcon className="w-16 h-16 text-stone-300" />
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-stone-500 font-mono">Codice</p>
              <p className="text-sm font-mono font-semibold text-stone-900">{product.code}</p>
            </div>
            {product.description_en && (
              <div>
                <p className="text-xs text-stone-500">English description</p>
                <p className="text-sm text-stone-700 italic">{product.description_en}</p>
              </div>
            )}
            {product.note && (
              <div>
                <p className="text-xs text-stone-500">Note</p>
                <p className="text-sm text-stone-700">{product.note}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-stone-500">Prezzo</p>
              <p className="text-3xl font-bold text-[#8A1C2A]">
                {product.price > 0 ? `${fm(product.price)}` : "—"}
                <span className="text-sm font-normal text-stone-500"> / {product.unit || "Kg"}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-1">Disponibilità</p>
              <Badge className={product.availability === "DISPONIBILE" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                {product.availability || "-"}
              </Badge>
            </div>
            <div className="border-t border-stone-200 pt-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-stone-700">Quantità</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                    className="bg-white border-stone-300"
                    data-testid="modal-qty-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-700">Unità di misura</Label>
                  <Select value={unit} onValueChange={setUnit}>
                    <SelectTrigger className="bg-white border-stone-300" data-testid="modal-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Kg">Kg</SelectItem>
                      <SelectItem value="Pz">Pz</SelectItem>
                      <SelectItem value="CT">CT (Cartone)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="bg-stone-50 rounded p-2 flex items-center justify-between">
                <span className="text-xs text-stone-600">Totale riga</span>
                <span className="font-bold text-[#8A1C2A]">{fm(lineTotal)}</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button
            onClick={() => onAdd(product, quantity, unit)}
            disabled={!product.price || product.price <= 0}
            className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
            data-testid="modal-add-to-cart-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" /> Aggiungi al carrello
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublicPortal;
export { ProductDetailModal };

